import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Settings, MapPin, Headphones, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../../services/dataSync';

const StructurePage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const organizationStructure = [
    {
      title: getText('about.structure.executive.title'),
      description: getText('about.structure.executive.desc'),
      icon: Users,
      members: [
        getText('about.structure.executive.members.chairman'),
        getText('about.structure.executive.members.viceChairman'),
        getText('about.structure.executive.members.secretary'),
        getText('about.structure.executive.members.treasurer')
      ],
      theme: 'from-blue-50 to-blue-100',
      iconTheme: 'from-blue-500 to-blue-700',
      textTheme: 'text-blue-600'
    },
    {
      title: getText('about.structure.technical.title'),
      description: getText('about.structure.technical.desc'),
      icon: Settings,
      members: [
        getText('about.structure.technical.members.safety'),
        getText('about.structure.technical.members.environmental'),
        getText('about.structure.technical.members.infrastructure'),
        getText('about.structure.technical.members.compliance')
      ],
      theme: 'from-emerald-50 to-emerald-100',
      iconTheme: 'from-emerald-500 to-emerald-700',
      textTheme: 'text-emerald-600'
    },
    {
      title: getText('about.structure.regional.title'),
      description: getText('about.structure.regional.desc'),
      icon: MapPin,
      members: [
        getText('about.structure.regional.mumbai'),
        getText('about.structure.regional.pune'),
        getText('about.structure.regional.nagpur'),
        getText('about.structure.regional.aurangabad'),
        getText('about.structure.regional.nashik')
      ],
      theme: 'from-purple-50 to-purple-100',
      iconTheme: 'from-purple-500 to-purple-700',
      textTheme: 'text-purple-600'
    },
    {
      title: getText('about.structure.support.title'),
      description: getText('about.structure.support.desc'),
      icon: Headphones,
      members: [
        getText('about.structure.support.members.admin'),
        getText('about.structure.support.members.technical'),
        getText('about.structure.support.members.field'),
        getText('about.structure.support.members.it')
      ],
      theme: 'from-amber-50 to-amber-100',
      iconTheme: 'from-amber-500 to-amber-700',
      textTheme: 'text-amber-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* About Us Button - Top */}
            <div className="mb-4">
              <Link to="/about" className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg">
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">{getText('nav.about.submenu.about')}</span>
              </Link>
            </div>
            
            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight">
                {getText('about.structure.title')}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 font-medium">
                {getText('about.structure.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Organization Structure */}
      <section className="section py-16 bg-white">
        <div className="container-narrow">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {organizationStructure.map((structure, index) => {
              const Icon = structure.icon;
              return (
                <div key={index} className="group">
                  <div className={`bg-gradient-to-br ${structure.theme} rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-secondary`}>
                    <div className={`bg-gradient-to-br ${structure.iconTheme} rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">{structure.title}</h3>
                    <p className="text-gray text-sm mb-4 leading-relaxed">{structure.description}</p>
                    <ul className="text-sm text-gray space-y-2">
                      {structure.members.map((member, memberIndex) => (
                        <li key={memberIndex} className="flex items-center justify-center">
                          <div className="w-2 h-2 bg-secondary rounded-full mr-2"></div>
                          {member}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA: Meet our Leadership Team */}
      <section className="section py-12 bg-gray-50">
        <div className="container-narrow text-center">
          <p className="text-lg text-gray-700 mb-4">
            {getText('about.structure.leadershipCta') || 'Meet the dedicated leaders who guide our strategic direction.'}
          </p>
          <Link
            to="/about/leadership-team"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {getText('nav.about.submenu.leadershipTeam') || 'Leadership Team'}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default StructurePage;
